import { useCallback, useContext, useState, type ReactNode } from 'react';

import { Modal } from '@cointracker/styleguide';
import { Button } from '@cointracker/styleguide/src/LoggedIn/Button';
import { useTranslation } from 'react-i18next';
import { ConfirmModalType } from './constants';
import { ConfirmModalContext, initialData } from './context';

export function ConfirmModal() {
  const { modalSettings, closeModal } = useContext(ConfirmModalContext);
  const { isOpen, title, body, onCancel, onConfirm, confirmButtonText, type } =
    modalSettings;
  const { t } = useTranslation();

  const handleClose = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      // modals' onHide doesn't provide an event target
      if (e) {
        e.preventDefault();
      }
      closeModal();
      onCancel();
    },
    [onCancel, closeModal],
  );
  const handleConfirm = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      closeModal();
      onConfirm();
    },
    [closeModal, onConfirm],
  );
  return (
    <Modal.Root open={isOpen} onOpenChange={handleClose as () => void}>
      <Modal.Content className="max-w-[450px]">
        <Modal.ContentHeader title={title} />
        <Modal.ContentBody>
          <Modal.ContentBody>{body}</Modal.ContentBody>
        </Modal.ContentBody>
        <Modal.ContentFooter className="flex flex-col">
          {type !== ConfirmModalType.ALERT && (
            <Button onClick={handleConfirm} fluid size="small">
              {confirmButtonText}
            </Button>
          )}
          {type === ConfirmModalType.ALERT && (
            <Button
              onClick={handleConfirm}
              fluid
              size="small"
              variant="negative"
            >
              {confirmButtonText}
            </Button>
          )}
          <Button onClick={handleClose} fluid size="small">
            {t('button.cancel')}
          </Button>
        </Modal.ContentFooter>
      </Modal.Content>
    </Modal.Root>
  );
}

interface ConfirmModalProviderProps {
  children: ReactNode | ReactNode[];
}

export function ConfirmModalProvider({ children }: ConfirmModalProviderProps) {
  const [modalSettings, setModalSettings] = useState(initialData);
  const closeModal = useCallback(() => {
    setModalSettings({ ...modalSettings, isOpen: false });
  }, [setModalSettings, modalSettings]);
  return (
    <ConfirmModalContext.Provider
      value={{ modalSettings, setModalSettings, closeModal }}
    >
      {children}
    </ConfirmModalContext.Provider>
  );
}
